// ═══════════════════════════════════════════════════════════════════════════
// basisStrength.js — is TODAY'S basis strong or weak for this delivery point,
// measured against the same delivery point's own history?
//
// Kevin, 2026-08-20: "use historical basis as a comparison for current basis
// and gauge its strength or weakness, in addition to futures strength or
// weakness."
//
// ── THE MEASUREMENT, AND WHY IT IS NOT A PLAIN PERCENTILE ─────────────────
// A basis quote is only comparable to another quote of the SAME THING. Four
// dimensions have to match before two numbers can sit in one distribution,
// and every one of them has already caused a wrong number in this repo or in
// the source data:
//
//   commodity + source (delivery point) + bid structure
//       A delivered bid and a picked-up bid differ by freight; two elevators
//       differ by their own demand. basis_calculated_daily spent six weeks
//       scoring ONE elevator as four markets because the sheet abbreviates
//       "Avon & Lakeville" differently per commodity section — see
//       basisMarketAliases.js. The slug is the identity; never pool two.
//
//   delivery MONTH
//       October basis and March basis are different markets in the same year.
//
//   POINT IN THE QUOTE'S LIFE (the seasonal dimension)
//       This is the one a naive percentile misses. New-crop December corn
//       basis quoted in the previous November is a different animal from the
//       same December basis quoted at harvest — the first is a forward offer
//       against unknown production, the second is a spot bid against a known
//       crop. Pooling them produces a percentile that mostly measures how far
//       from delivery the quote was. So a target is compared only against
//       historical quotes made a similar distance from their own delivery
//       month, exactly as futuresContractHistory.js scores a contract at a
//       point in contract life. Same idea, different measure — deliberately
//       the same shape so the two gauges read alike.
//
// ── CROP YEARS ARE EQUALIZED, NOT POOLED ─────────────────────────────────
// The transcribed archive is far denser in some years than others: 2025 and
// 2026 carry many sheets, 2021 carries one. A raw percentile over pooled
// observations is therefore mostly a statement about 2025-26, wearing the
// label "history". The source package's own data contract names this:
// "Crop years with more source sheets must not receive accidental extra
// statistical weight."
//
// So every crop year contributes the SAME total weight regardless of how many
// sheets survive from it: an observation's weight is 1/(observations from its
// crop year). The percentile is the weighted fraction at or below the target.
// With one sheet per year this is identical to a plain percentile; the
// weighting only ever removes an artefact of how many photographs exist.
//
// ── IT REFUSES TO ANSWER MORE OFTEN THAN IT ANSWERS ──────────────────────
// Requiring both a minimum sample AND a minimum number of distinct crop years
// is the same bar scoreContractAtLifePoint sets, for the same reason: one
// year's few observations are a story about that year, not a history. Of the
// 458 cohorts in the archive only 13 span 5+ crop years, so most cells will
// correctly return available:false. "insufficient_history" is the honest
// answer the data contract asks for — never a precise-looking number from a
// thin sample.
//
// A NO_BID is excluded from every distribution (it is a market state, not a
// low number) but is COUNTED and reported, because "this buyer has refused
// this window before" is itself a fact about strength.
//
// PURE — no fetch, no React, no Supabase.
// ═══════════════════════════════════════════════════════════════════════════

import { opportunityCategory } from "./grainCurve";

// ── DEFAULT_WINDOW_DAYS — MEASURED, not chosen ────────────────────────────
// How far either side of the target's point-in-life a historical quote may sit
// and still be comparable. The archive is 39 PHOTOGRAPHED SHEETS over five
// crop years — roughly seven a year — so a tight window usually catches one or
// two crop years and fails the 3-year bar. Measured against the live 183-cell
// board on 2026-08-20, cells that produce a reading:
//
//     ±14d   0.4% of cohorts        ±21d   0 of 183 board cells
//     ±30d   2.5%                   ±30d  13 of 183  (7%)
//     ±45d   3.4%                   ±45d  25 of 183  (14%)
//     ±60d   4.3%                   ±60d  26 of 183  (14%)
//
// ±45 is where the curve flattens: ±60 buys one more cell while blurring a
// quote made 3 months out against one made 5. So ±45.
//
// THE LOW COVERAGE IS THE DATA, NOT A BUG IN THE MEASURE. 86% of the board
// correctly answers "not enough comparable history" today, and that is the
// answer the source package's own contract asks for. Coverage rises with every
// sheet imported — which is now automatic — rather than by loosening the gate.
export const DEFAULT_WINDOW_DAYS = 45;

const LETTER_MONTH = { F: 0, G: 1, H: 2, J: 3, K: 4, M: 5, N: 6, Q: 7, U: 8, V: 9, X: 10, Z: 11 };
const MONTH_NAME = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ── parseContract — "Z26" -> { letter:"Z", monthIndex:11, year:2026 }.
// Returns null for anything else, INCLUDING the empty contract a handful of
// archived rows carry. A row with no contract cannot be placed in delivery
// time at all, so it can never enter a lead-time comparison — it is dropped
// and counted, never assigned a guessed delivery month.
export function parseContract(code) {
  const m = String(code || "").trim().toUpperCase().match(/^([FGHJKMNQUVXZ])(\d{2})$/);
  if (!m) return null;
  return { letter: m[1], monthIndex: LETTER_MONTH[m[1]], year: 2000 + Number(m[2]) };
}

// ── leadDays — calendar days from the observation to the 1st of the delivery
// month. UTC on both sides so a midnight/TZ edge cannot shift it by a day.
// Negative means the quote was made after delivery started (an old-crop bid
// still posted into the delivery month), which is a real and comparable state.
export function leadDays({ contract, obsDate }) {
  const c = parseContract(contract);
  if (!c || !obsDate) return null;
  const parts = String(obsDate).slice(0, 10).split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
  return Math.round((Date.UTC(c.year, c.monthIndex, 1) - Date.UTC(parts[0], parts[1] - 1, parts[2])) / 86400000);
}

// ── normalizeObservation — one archived row (basis_sheet_observations shape,
// snake or camel) into what the scorer needs. Returns null when the row cannot
// be placed in delivery time or carries no usable basis; the caller counts
// those rather than dropping them silently.
export function normalizeObservation(raw) {
  if (!raw) return null;
  const contract = raw.futures_contract ?? raw.futuresContract ?? raw.deliveryContract ?? null;
  const obsDate = raw.sheet_date ?? raw.sheetDate ?? raw.bid_date ?? raw.bidDate ?? null;
  const c = parseContract(contract);
  if (!c || !obsDate) return null;
  const noBid = raw.no_bid ?? raw.noBid ?? false;
  const basis = raw.basis ?? raw.delivery_basis ?? raw.deliveryBasis ?? null;
  const lead = leadDays({ contract, obsDate });
  if (lead == null) return null;
  return {
    // `crop` is what basisHistoryImport.js's normalizer emits and `commodity`
    // is what basis_sheet_observations stores. Both are accepted because both
    // are real callers — reading only one of them silently produced an empty
    // commodity on every archived row, which matched nothing and made the whole
    // archive look like it held no comparable history.
    commodity: String(raw.commodity ?? raw.crop ?? "").trim().toLowerCase(),
    source: String(raw.source || "").trim(),
    bidStructure: String(raw.bid_structure ?? raw.bidStructure ?? raw.term ?? "").trim().toLowerCase(),
    contract, letter: c.letter, deliveryYear: c.year,
    // The delivery year is the crop-year axis for equalization. The stored
    // crop_year column is the label the SHEET used and is null on every live
    // import (the board has no crop-year column), so it cannot be the key —
    // the delivery year is always derivable and always present.
    obsDate: String(obsDate).slice(0, 10),
    leadDays: lead,
    noBid: !!noBid,
    basis: noBid ? null : (basis == null ? null : Number(basis)),
  };
}

// ── filterComparableBasis — the ONE comparability rule, extracted so the
// scorer and any caller wanting to show the sample itself use the same gate
// rather than two that can drift.
export function filterComparableBasis({
  commodity, source, bidStructure, letter, targetDeliveryYear, targetLeadDays,
  history = [], windowDays = DEFAULT_WINDOW_DAYS,
}) {
  const com = String(commodity || "").trim().toLowerCase();
  const src = String(source || "").trim();
  const str = String(bidStructure || "").trim().toLowerCase();
  return (history || []).filter((r) => {
    if (!r) return false;
    if (r.commodity !== com || r.source !== src) return false;
    // An unknown structure never matches. It is not a wildcard: an unlabeled
    // row could be either side of a freight difference, so admitting it would
    // widen the sample with rows that might not belong.
    if (!str || r.bidStructure !== str) return false;
    if (r.letter !== letter) return false;
    // Never score a delivery against its own year — that would include the
    // target's own prior quotes and measure the season, not the history.
    if (Number(r.deliveryYear) === Number(targetDeliveryYear)) return false;
    if (r.noBid || r.basis == null || !Number.isFinite(r.basis)) return false;
    if (targetLeadDays == null || r.leadDays == null) return false;
    return Math.abs(r.leadDays - targetLeadDays) <= windowDays;
  });
}

// ── cropYearEqualizedPercentile — see header. Each delivery year contributes
// total weight 1, split evenly among its own observations, so a year with 40
// sheets does not outvote a year with 2. Exported for the checker to assert
// against a hand-computed case.
export function cropYearEqualizedPercentile(samples, target) {
  if (!samples.length) return null;
  const perYear = new Map();
  for (const s of samples) perYear.set(s.deliveryYear, (perYear.get(s.deliveryYear) || 0) + 1);
  let atOrBelow = 0, total = 0;
  for (const s of samples) {
    const w = 1 / perYear.get(s.deliveryYear);
    total += w;
    if (s.basis <= target) atOrBelow += w;
  }
  return total > 0 ? Math.round((atOrBelow / total) * 100) : null;
}

// ── scoreBasisAtLead — the reading. Returns available:false with real counts
// whenever the archive cannot defensibly answer, never a manufactured number.
export function scoreBasisAtLead({
  commodity, source, bidStructure, contract, obsDate, targetBasis,
  history = [], windowDays = DEFAULT_WINDOW_DAYS, minSamples = 5, minCropYears = 3,
}) {
  const c = parseContract(contract);
  const lead = leadDays({ contract, obsDate });
  if (!c) return { available: false, reason: `no delivery contract on this quote — it cannot be placed against history`, sampleSize: 0, cropYears: 0, windowDays };
  if (targetBasis == null || !Number.isFinite(Number(targetBasis))) {
    return { available: false, reason: "no basis on this quote to score", sampleSize: 0, cropYears: 0, windowDays };
  }
  const comparable = filterComparableBasis({
    commodity, source, bidStructure, letter: c.letter,
    targetDeliveryYear: c.year, targetLeadDays: lead, history, windowDays,
  });
  const years = new Set(comparable.map((r) => r.deliveryYear));
  const deliveryLabel = `${MONTH_NAME[c.monthIndex]} ${c.year}`;

  if (comparable.length < minSamples || years.size < minCropYears) {
    return {
      available: false,
      reason: comparable.length
        ? `only ${comparable.length} comparable observation(s) across ${years.size} crop year(s) within ±${windowDays}d of this point in the quote's life (need ${minSamples} across ${minCropYears})`
        : `no ${deliveryLabel.split(" ")[0]}-delivery history for this market at ${Math.round(lead / 30)} months out`,
      sampleSize: comparable.length, cropYears: years.size, windowDays,
      leadDays: lead, deliveryLabel,
    };
  }

  const percentile = cropYearEqualizedPercentile(comparable, Number(targetBasis));
  const vals = comparable.map((r) => r.basis).sort((a, b) => a - b);
  return {
    available: true,
    percentile,
    sampleSize: comparable.length,
    cropYears: years.size,
    comparableYears: [...years].sort((a, b) => a - b),
    windowDays, leadDays: lead, deliveryLabel,
    low: vals[0], high: vals[vals.length - 1],
    median: vals[Math.floor(vals.length / 2)],
  };
}

// ── buildBasisStrengthGauge — gauge-shaped wrapper using the SAME 3-way
// opportunityCategory() the futures gauges use. One scale across the page, so
// "strong" means the same thing whichever gauge a reader is looking at.
//
// A STRONG BASIS IS GOOD FOR THE SELLER, which is the same direction as a
// strong futures reading — both mean "this is a high number for this market",
// so the two can sit side by side without one needing to be read backwards.
export function buildBasisStrengthGauge(score, marketLabel = null) {
  if (!score || !score.available) {
    return {
      state: "unknown", fillPct: null, category: "unknown",
      label: "not enough comparable history to score this basis",
      reason: (score && score.reason) || "no score computed",
      sampleSize: (score && score.sampleSize) || 0,
      cropYears: (score && score.cropYears) || 0,
      windowDays: (score && score.windowDays) ?? null,
      marketLabel,
    };
  }
  const cat = opportunityCategory(score.percentile / 100);
  return {
    state: "known", fillPct: score.percentile, category: cat,
    label: `${cat.toUpperCase()} · ${score.percentile}th percentile of ${score.cropYears} prior crop years`,
    detail: `n=${score.sampleSize} · ${score.deliveryLabel} delivery · ±${score.windowDays}d of ${Math.round(score.leadDays / 30)} months out · range ${score.low.toFixed(2)} to ${score.high.toFixed(2)}`,
    sampleSize: score.sampleSize, cropYears: score.cropYears,
    comparableYears: score.comparableYears, windowDays: score.windowDays,
    low: score.low, high: score.high, median: score.median,
    marketLabel,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// aggregateBasisStrength — "is CORN BASIS strong or weak right now", as one
// market-level reading across every location and both bid structures, rather
// than one elevator's number standing in for the region.
//
// Kevin, 2026-08-20: "I want to see relative basis strength for all locations,
// picked up, delivered, vs other points in history... an aggregate of corn
// basis now compared to aggregate of points in history."
//
// ── WHY NOT A MEDIAN OF PER-LOCATION PERCENTILES ─────────────────────────
// That was the first design and it is measurably useless today: of the six
// (location x structure) cohorts on the live corn board, exactly ONE clears
// the 3-crop-year bar at any contract — Geneva. A "median" of one location is
// the same single-location reading, wearing an aggregate's name.
//
// ── WHY NOT AN AVERAGE OF RAW BASIS LEVELS ───────────────────────────────
// Because the panel is UNBALANCED and the roster moves. Across the Z-contract
// window the number of locations reporting on a sheet runs 1 to 24 (median
// 12): Del Mar stops appearing after 2024, Pickup West starts 2025-10, LeRoy
// starts 2024-10. A raw average would rise the day a strong-basis location
// joins the sheet and fall the day it leaves — a composition change rendered
// as a market move. It would also add a delivered level to a pickup level,
// which differ by freight and are not the same quantity.
//
// ── WHAT THIS DOES INSTEAD: EACH LOCATION AGAINST ITS OWN NORMAL ─────────
// Every observation is expressed as a deviation from ITS OWN location's mean
// for this contract and point-in-life. A pickup column is therefore never
// compared to a delivered column in level terms — only "how far is this
// location from where it usually sits", which is the same unit for both. A
// sheet's aggregate is the mean of those deviations, so a location joining or
// leaving moves the index only by its own deviation, not by its level.
//
// Then today's aggregate is ranked against the historical sheet aggregates,
// crop-year-equalized so a heavily-photographed year cannot carry the answer.
//
// ── A LOCATION WITH NO HISTORY IS EXCLUDED AND NAMED ─────────────────────
// It cannot have a deviation, because it has no normal to deviate from.
// Including its raw level would be the composition bias above in its purest
// form. This is not hypothetical: WNY Energy, Deerfield and every other
// non-Howlett source begin 2026-05-28 or later, so today none of them can
// contribute to a multi-year reading and all of them are reported by name in
// `excluded` rather than silently folded in.
export function aggregateBasisStrength({
  commodity, contract, obsDate, current = [], history = [],
  windowDays = DEFAULT_WINDOW_DAYS, minPerLocation = 2, minDates = 5, minCropYears = 3,
}) {
  const c = parseContract(contract);
  const lead = leadDays({ contract, obsDate });
  const com = String(commodity || "").trim().toLowerCase();
  if (!c || lead == null) {
    return { available: false, reason: "no delivery contract on this selection", scored: 0, excluded: [] };
  }

  // Comparable history: same commodity, same delivery MONTH, similar distance
  // from delivery, and never this contract's own delivery year.
  const win = (history || []).filter((r) =>
    r && r.commodity === com && r.letter === c.letter
    && Number(r.deliveryYear) !== Number(c.year)
    && !r.noBid && r.basis != null && Number.isFinite(r.basis)
    && r.leadDays != null && Math.abs(r.leadDays - lead) <= windowDays);

  // Each location's OWN normal over that window. A location seen once cannot
  // supply a normal — its single observation IS its mean, so its deviation is
  // definitionally zero and it would drag every aggregate toward the middle.
  const byLoc = new Map();
  for (const r of win) {
    const k = `${r.source}|${r.bidStructure}`;
    if (!byLoc.has(k)) byLoc.set(k, []);
    byLoc.get(k).push(r);
  }
  const normal = new Map();
  for (const [k, rows] of byLoc) {
    if (rows.length < minPerLocation) continue;
    normal.set(k, rows.reduce((s, r) => s + r.basis, 0) / rows.length);
  }

  // Historical sheet aggregates: mean deviation across the locations that
  // reported that day and have a normal.
  const byDate = new Map();
  for (const r of win) {
    const k = `${r.source}|${r.bidStructure}`;
    if (!normal.has(k)) continue;
    if (!byDate.has(r.obsDate)) byDate.set(r.obsDate, { devs: [], year: r.deliveryYear });
    byDate.get(r.obsDate).devs.push(r.basis - normal.get(k));
  }
  const samples = [...byDate.entries()]
    .map(([d, v]) => ({ obsDate: d, deliveryYear: v.year, basis: v.devs.reduce((s, x) => s + x, 0) / v.devs.length }))
    .filter((s) => Number.isFinite(s.basis));

  // Today, through the SAME transform.
  const contributors = [], excluded = [];
  let devSum = 0, devN = 0;
  for (const cur of current || []) {
    const k = `${cur.source}|${String(cur.bidStructure || "").toLowerCase()}`;
    const label = cur.sourceLabel || cur.source;
    if (cur.basis == null || !Number.isFinite(Number(cur.basis))) { excluded.push({ label, reason: "no basis posted" }); continue; }
    if (!normal.has(k)) { excluded.push({ label, reason: "no comparable history for this location at this contract" }); continue; }
    const dev = Number(cur.basis) - normal.get(k);
    contributors.push({ label, source: cur.source, bidStructure: cur.bidStructure, basis: Number(cur.basis), dev });
    devSum += dev; devN++;
  }

  const years = new Set(samples.map((s) => s.deliveryYear));
  if (!devN || samples.length < minDates || years.size < minCropYears) {
    return {
      available: false,
      reason: !devN
        ? "no posted location has comparable history at this contract"
        : `only ${samples.length} comparable sheet date(s) across ${years.size} crop year(s) (need ${minDates} across ${minCropYears})`,
      scored: devN, sampleDates: samples.length, cropYears: years.size,
      contributors, excluded, windowDays, leadDays: lead,
    };
  }

  const todayDev = devSum / devN;
  const percentile = cropYearEqualizedPercentile(samples, todayDev);
  // Per prior crop year, so the answer can be shown as a comparison rather
  // than a rank. `n` rides along because these are NOT equally solid — the
  // 2021 sheets are one observation where 2025 is forty-six, and a year
  // drawn at the same weight as the others would overstate what it knows.
  const perYear = new Map();
  for (const s of samples) {
    if (!perYear.has(s.deliveryYear)) perYear.set(s.deliveryYear, []);
    perYear.get(s.deliveryYear).push(s.basis);
  }
  const yearRows = [...perYear.entries()]
    .map(([y, v]) => ({ deliveryYear: y, dev: v.reduce((a, b) => a + b, 0) / v.length, sheets: v.length }))
    .sort((a, b) => a.deliveryYear - b.deliveryYear);
  const devs = samples.map((s) => s.basis).sort((a, b) => a - b);
  return {
    available: true,
    percentile,
    scored: devN,
    locations: contributors.length,
    sampleDates: samples.length,
    cropYears: years.size,
    comparableYears: [...years].sort((a, b) => a - b),
    todayDev, years: yearRows, windowDays, leadDays: lead,
    deliveryLabel: `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][c.monthIndex]} ${c.year}`,
    low: devs[0], high: devs[devs.length - 1],
    contributors, excluded,
  };
}
